/**
 * Dashboard-specific visual widgets + formatters used by the workspace
 * dashboards (CRM / Finance / Service / AI). Page chrome (KpiCard/KpiRow,
 * SectionCard, DataTable) now comes from the design kit (`components/patterns`)
 * — only the bespoke visuals live here.
 */

import { ReactNode } from 'react'

export function VisionRibbon() {
  return (
    <div className="rounded-lg bg-amber-500/10 ring-1 ring-amber-500/30 px-4 py-2.5 text-xs text-amber-300 flex items-center gap-2">
      <span className="text-[10px] uppercase tracking-wider font-bold bg-amber-500/20 text-amber-200 px-1.5 py-0.5 rounded">prototype</span>
      <span className="text-amber-200/90">
        Static mock data — wired up so you can see the shape of the module before we commit to the real backend.
      </span>
    </div>
  )
}

/* ─── Column grids for blocks ───────────────────────────────────────── */

export function TwoCol({ children }: { children: ReactNode }) {
  return <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">{children}</div>
}

export function ThreeCol({ children }: { children: ReactNode }) {
  return <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">{children}</div>
}

/* ─── Avatar with coloured fallback bubble ──────────────────────────── */

const palette: Record<string, string> = {
  rose:    '#f43f5e',
  indigo:  '#6366f1',
  amber:   '#f59e0b',
  sky:     '#0ea5e9',
  emerald: '#10b981',
  fuchsia: '#d946ef',
  violet:  '#8b5cf6',
  orange:  '#f97316',
  teal:    '#14b8a6',
  lime:    '#84cc16',
  cyan:    '#06b6d4',
  red:     '#ef4444',
}

export function Avatar({
  name,
  colourKey = 'indigo',
  size = 28,
  online,
}: {
  name: string
  colourKey?: string
  size?: number
  online?: boolean
}) {
  const initials = name
    .split(' ')
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase()
  const bg = palette[colourKey] || palette.indigo
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <div
        className="w-full h-full rounded-full text-white font-bold flex items-center justify-center"
        style={{ backgroundColor: bg, fontSize: Math.round(size * 0.38) }}
      >
        {initials}
      </div>
      {online !== undefined && (
        <span
          className="absolute -bottom-0.5 -right-0.5 rounded-full ring-2 ring-card"
          style={{
            width: Math.max(8, size * 0.32),
            height: Math.max(8, size * 0.32),
            background: online ? '#10b981' : '#71717a',
          }}
        />
      )}
    </div>
  )
}

/* ─── Pill / badge helpers ──────────────────────────────────────────── */

export function Pill({
  children,
  tone = 'muted',
}: {
  children: ReactNode
  tone?: 'muted' | 'primary' | 'success' | 'warn' | 'danger' | 'info'
}) {
  const map: Record<string, string> = {
    muted:   'bg-muted/50 text-muted-foreground',
    primary: 'bg-primary/15 text-primary',
    success: 'bg-emerald-500/15 text-emerald-400',
    warn:    'bg-amber-500/15 text-amber-400',
    danger:  'bg-rose-500/15 text-rose-400',
    info:    'bg-sky-500/15 text-sky-400',
  }
  return (
    <span className={`text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded ${map[tone]}`}>
      {children}
    </span>
  )
}

/* ─── Sparkline (svg, no deps) ──────────────────────────────────────── */

export function Sparkline({
  data,
  width = 220,
  height = 50,
  stroke = 'currentColor',
  fill = true,
}: {
  data: number[]
  width?: number
  height?: number
  stroke?: string
  fill?: boolean
}) {
  if (data.length === 0) return null
  const min = Math.min(...data)
  const max = Math.max(...data)
  const range = max - min || 1
  const step = width / (data.length - 1 || 1)
  const points = data.map((v, i) => `${i * step},${height - ((v - min) / range) * (height - 4) - 2}`).join(' ')
  return (
    <svg width={width} height={height} className="overflow-visible">
      {fill && (
        <polygon
          points={`0,${height} ${points} ${width},${height}`}
          fill="currentColor"
          opacity="0.18"
        />
      )}
      <polyline points={points} fill="none" stroke={stroke} strokeWidth="1.75" strokeLinejoin="round" />
    </svg>
  )
}

/* ─── Funnel (horizontal bars descending) ───────────────────────────── */

export function Funnel({
  stages,
}: {
  stages: Array<{ label: string; value: number; sub?: string }>
}) {
  const max = Math.max(...stages.map((s) => s.value), 1)
  return (
    <div className="space-y-2">
      {stages.map((s) => {
        const pct = (s.value / max) * 100
        return (
          <div key={s.label}>
            <div className="flex items-baseline justify-between text-xs mb-1">
              <span className="text-foreground font-medium">{s.label}</span>
              <span className="text-muted-foreground tabular-nums">
                {s.value.toLocaleString()}
                {s.sub && <span className="ml-2">· {s.sub}</span>}
              </span>
            </div>
            <div className="h-2.5 bg-muted/40 rounded-full overflow-hidden">
              <div className="h-full bg-primary/80 rounded-full" style={{ width: `${pct}%` }} />
            </div>
          </div>
        )
      })}
    </div>
  )
}

/* ─── Currency formatting ───────────────────────────────────────────── */

export function fmtMoney(value: number, currency = 'USD'): string {
  const symbol = currency === 'USD' ? '$' : currency
  if (Math.abs(value) >= 1_000_000) return `${symbol}${(value / 1_000_000).toFixed(1)}M`
  if (Math.abs(value) >= 10_000) return `${symbol}${Math.round(value / 1000)}k`
  if (Math.abs(value) >= 1000) return `${symbol}${(value / 1000).toFixed(1)}k`
  return `${symbol}${value.toLocaleString()}`
}
